export type RestaurantCustomerConversationRole = "customer" | "assistant" | "operator" | "system";
export type RestaurantCustomerRoutingMode = "ai" | "human";

export interface RestaurantCustomerConversationMessage {
  readonly id: string;
  readonly role: RestaurantCustomerConversationRole;
  readonly text: string;
  readonly at: string;
}

export interface RestaurantCustomerConversationState {
  readonly version: 1;
  readonly conversationId?: string;
  readonly workingState?: Readonly<Record<string, unknown>> | null;
  readonly routingMode: RestaurantCustomerRoutingMode;
  readonly handoffReason: string | null;
  readonly messages: readonly RestaurantCustomerConversationMessage[];
  readonly updatedAt: string;
}

const STORAGE_PREFIX = "clickdialog:sales-demo:restaurant-customer-conversation:";
const RESET_STORAGE_PREFIX = "clickdialog:sales-demo:restaurant-customer-reset:";
export const RESTAURANT_CUSTOMER_CONVERSATION_EVENT = "clickdialog:restaurant-customer-conversation";
export const RESTAURANT_CUSTOMER_RESET_EVENT = "clickdialog:restaurant-customer-reset";

function key(slug: string): string { return `${STORAGE_PREFIX}${slug}`; }
export function restaurantCustomerResetStorageKey(slug: string): string { return `${RESET_STORAGE_PREFIX}${slug}`; }
function nowIso(): string { return new Date().toISOString(); }
function uid(prefix: string): string { return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; }

export function createRestaurantCustomerConversationSeed(businessName: string, at = nowIso()): RestaurantCustomerConversationState {
  return Object.freeze({
    version: 1,
    conversationId: uid("conversation"),
    workingState: null,
    routingMode: "ai",
    handoffReason: null,
    messages: Object.freeze([Object.freeze({ id: "customer-welcome", role: "assistant", text: `Hola 👋 Soy el asistente de ${businessName}. Puedo ayudarte con la carta, un pedido o una reserva.`, at })]),
    updatedAt: at,
  });
}

function valid(value: unknown): value is RestaurantCustomerConversationState {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  if (record.conversationId != null && (typeof record.conversationId !== "string" || !/^[A-Za-z0-9_-]{8,128}$/.test(record.conversationId))) return false;
  if (record.workingState != null && (typeof record.workingState !== "object" || Array.isArray(record.workingState))) return false;
  if (record.version !== 1 || (record.routingMode !== "ai" && record.routingMode !== "human") || !Array.isArray(record.messages)) return false;
  if (record.handoffReason !== null && typeof record.handoffReason !== "string") return false;
  return record.messages.every((entry) => typeof entry === "object" && entry !== null && !Array.isArray(entry)
    && typeof (entry as Record<string, unknown>).id === "string"
    && ["customer", "assistant", "operator", "system"].includes(String((entry as Record<string, unknown>).role))
    && typeof (entry as Record<string, unknown>).text === "string"
    && typeof (entry as Record<string, unknown>).at === "string");
}

function notify(slug: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(RESTAURANT_CUSTOMER_CONVERSATION_EVENT, { detail: { slug } }));
}

function notifyReset(slug: string, marker: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(RESTAURANT_CUSTOMER_RESET_EVENT, { detail: { slug, marker } }));
}

export function appendRestaurantCustomerConversationStateMessage(
  current: RestaurantCustomerConversationState,
  input: { readonly role: RestaurantCustomerConversationRole; readonly text: string; readonly id: string; readonly at?: string },
): RestaurantCustomerConversationState {
  const text = input.text.trim();
  if (!text || current.messages.some((message) => message.id === input.id)) return current;
  const at = input.at ?? nowIso();
  return Object.freeze({
    ...current,
    messages: Object.freeze([...current.messages, Object.freeze({ id: input.id, role: input.role, text, at })].slice(-80)),
    updatedAt: at,
  });
}

export function transitionRestaurantCustomerHandoff(
  current: RestaurantCustomerConversationState,
  input: { readonly reason?: string | null; readonly bridgeId?: string; readonly at?: string } = {},
): RestaurantCustomerConversationState {
  if (current.routingMode === "human") return current;
  const at = input.at ?? nowIso();
  const reason = input.reason?.trim() || "El cliente pidió hablar con una persona.";
  const bridgeId = input.bridgeId ?? "handoff-bridge-active";
  const withBridge = appendRestaurantCustomerConversationStateMessage(current, {
    role: "system",
    id: bridgeId,
    text: "El asistente quedó pausado. Una persona del equipo puede continuar esta conversación.",
    at,
  });
  return Object.freeze({ ...withBridge, routingMode: "human", handoffReason: reason, updatedAt: at });
}

export function transitionRestaurantCustomerOperatorReply(
  current: RestaurantCustomerConversationState,
  input: { readonly text: string; readonly id: string; readonly at?: string },
): RestaurantCustomerConversationState {
  if (current.routingMode !== "human") throw new Error("customer_conversation_not_in_human_control");
  return appendRestaurantCustomerConversationStateMessage(current, { ...input, role: "operator" });
}

export function transitionRestaurantCustomerResumeAi(
  current: RestaurantCustomerConversationState,
  at = nowIso(),
): RestaurantCustomerConversationState {
  if (current.routingMode === "ai") return current;
  const activeBridge = [...current.messages].reverse().find((message) => message.role === "system" && message.id.startsWith("handoff-"));
  const resumeId = `resume-${activeBridge?.id ?? current.updatedAt}`;
  const withResume = appendRestaurantCustomerConversationStateMessage(current, {
    role: "system",
    id: resumeId,
    text: "El equipo reactivó el asistente automático.",
    at,
  });
  return Object.freeze({ ...withResume, routingMode: "ai", handoffReason: null, updatedAt: at });
}

export function loadRestaurantCustomerConversation(slug: string, businessName: string): RestaurantCustomerConversationState {
  if (typeof window === "undefined" || !window.localStorage) return createRestaurantCustomerConversationSeed(businessName);
  const raw = window.localStorage.getItem(key(slug));
  if (!raw) {
    const initial = createRestaurantCustomerConversationSeed(businessName);
    window.localStorage.setItem(key(slug), JSON.stringify(initial));
    return initial;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (valid(parsed)) {
      if (parsed.conversationId) return parsed;
      const migrated = { ...parsed, conversationId: uid("conversation"), workingState: null };
      window.localStorage.setItem(key(slug), JSON.stringify(migrated));
      return migrated;
    }
  } catch {
    // fall through to deterministic reset
  }
  const initial = createRestaurantCustomerConversationSeed(businessName);
  window.localStorage.setItem(key(slug), JSON.stringify(initial));
  return initial;
}

export function saveRestaurantCustomerConversation(slug: string, state: RestaurantCustomerConversationState): RestaurantCustomerConversationState {
  const next = Object.freeze({ ...state, messages: Object.freeze([...state.messages].slice(-80)) });
  if (typeof window !== "undefined" && window.localStorage) window.localStorage.setItem(key(slug), JSON.stringify(next));
  notify(slug);
  return next;
}

/** A turn may apply only to exactly the conversation snapshot it started from. */
export function canApplyRestaurantCustomerTurn(started: RestaurantCustomerConversationState, current: RestaurantCustomerConversationState): boolean {
  return current.routingMode === "ai" && started.conversationId === current.conversationId
    && JSON.stringify(started) === JSON.stringify(current);
}

export function appendRestaurantCustomerConversationMessage(input: {
  readonly slug: string;
  readonly businessName: string;
  readonly role: RestaurantCustomerConversationRole;
  readonly text: string;
  readonly id?: string;
}): RestaurantCustomerConversationState {
  const current = loadRestaurantCustomerConversation(input.slug, input.businessName);
  const next = appendRestaurantCustomerConversationStateMessage(current, {
    role: input.role,
    text: input.text,
    id: input.id ?? uid(input.role),
  });
  return next === current ? current : saveRestaurantCustomerConversation(input.slug, next);
}

export function requestRestaurantCustomerHandoff(input: {
  readonly slug: string;
  readonly businessName: string;
  readonly reason?: string | null;
  readonly bridgeId?: string;
}): RestaurantCustomerConversationState {
  const current = loadRestaurantCustomerConversation(input.slug, input.businessName);
  const next = transitionRestaurantCustomerHandoff(current, input);
  return next === current ? current : saveRestaurantCustomerConversation(input.slug, next);
}

export function appendRestaurantOperatorReply(input: {
  readonly slug: string;
  readonly businessName: string;
  readonly text: string;
  readonly id?: string;
}): RestaurantCustomerConversationState {
  const current = loadRestaurantCustomerConversation(input.slug, input.businessName);
  const next = transitionRestaurantCustomerOperatorReply(current, {
    text: input.text,
    id: input.id ?? uid("operator"),
  });
  return next === current ? current : saveRestaurantCustomerConversation(input.slug, next);
}

export function resumeRestaurantCustomerAi(slug: string, businessName: string): RestaurantCustomerConversationState {
  const current = loadRestaurantCustomerConversation(slug, businessName);
  const next = transitionRestaurantCustomerResumeAi(current);
  return next === current ? current : saveRestaurantCustomerConversation(slug, next);
}

export function resetRestaurantCustomerConversation(slug: string, businessName: string): RestaurantCustomerConversationState {
  const initial = createRestaurantCustomerConversationSeed(businessName);
  if (typeof window !== "undefined" && window.localStorage) window.localStorage.setItem(key(slug), JSON.stringify(initial));
  notify(slug);
  return initial;
}

export function resetRestaurantCustomerExperienceSession(slug: string, businessName: string): RestaurantCustomerConversationState {
  const initial = resetRestaurantCustomerConversation(slug, businessName);
  const marker = nowIso();
  if (typeof window !== "undefined" && window.localStorage) window.localStorage.setItem(restaurantCustomerResetStorageKey(slug), marker);
  notifyReset(slug, marker);
  return initial;
}
/** Raw existing business storage, for detecting writes while an asynchronous turn runs. */
export function restaurantCustomerBusinessSnapshot(slug: string): string {
  return JSON.stringify(["restaurant", "restaurant-v5"].map((owner) =>
    window.localStorage.getItem(`clickdialog:sales-demo:${owner}:${slug}`)));
}
